import type { JsonValue } from '../components/JsonTreeEditor';
import { type JsonPath, pathToKey } from './jsonPath';

export type JsonDiffKind = '新增' | '删除' | '修改';

export interface JsonDiffEntry {
	kind: JsonDiffKind;
	pathKey: string;
	beforeText: string;
	afterText: string;
}

export interface JsonDiffSummary {
	items: JsonDiffEntry[];
	added: number;
	removed: number;
	changed: number;
}

function isObjectValue(
	value: JsonValue,
): value is { [key: string]: JsonValue } {
	return !Array.isArray(value) && typeof value === 'object' && value !== null;
}

function hasOwnKey(value: { [key: string]: JsonValue }, key: string): boolean {
	return Object.keys(value).includes(key);
}

function isEqualJsonValue(left: JsonValue, right: JsonValue): boolean {
	if (left === right) {
		return true;
	}

	if (Array.isArray(left) && Array.isArray(right)) {
		if (left.length !== right.length) {
			return false;
		}

		return left.every((item, index) => isEqualJsonValue(item, right[index]));
	}

	if (isObjectValue(left) && isObjectValue(right)) {
		const leftKeys = Object.keys(left);
		const rightKeys = Object.keys(right);

		if (leftKeys.length !== rightKeys.length) {
			return false;
		}

		return leftKeys.every(
			(key) => hasOwnKey(right, key) && isEqualJsonValue(left[key], right[key]),
		);
	}

	return false;
}

function formatJsonValue(value: JsonValue) {
	if (value === null) {
		return 'null';
	}

	if (typeof value === 'string') {
		return JSON.stringify(value);
	}

	const text = JSON.stringify(value, null, 2);

	if (!text) {
		return String(value);
	}

	return text.length > 240 ? `${text.slice(0, 240)}...` : text;
}

function pushDiff(
	items: JsonDiffEntry[],
	kind: JsonDiffKind,
	path: JsonPath,
	beforeValue: JsonValue | undefined,
	afterValue: JsonValue | undefined,
) {
	items.push({
		kind,
		pathKey: pathToKey(path),
		beforeText: beforeValue === undefined ? '-' : formatJsonValue(beforeValue),
		afterText: afterValue === undefined ? '-' : formatJsonValue(afterValue),
	});
}

function walkDiff(
	beforeValue: JsonValue | undefined,
	afterValue: JsonValue | undefined,
	path: JsonPath,
	items: JsonDiffEntry[],
) {
	if (beforeValue !== undefined && afterValue !== undefined) {
		if (isEqualJsonValue(beforeValue, afterValue)) {
			return;
		}

		const beforeIsArray = Array.isArray(beforeValue);
		const afterIsArray = Array.isArray(afterValue);
		const beforeIsObject = isObjectValue(beforeValue);
		const afterIsObject = isObjectValue(afterValue);

		if (beforeIsArray && afterIsArray) {
			const maxLength = Math.max(beforeValue.length, afterValue.length);
			for (let index = 0; index < maxLength; index += 1) {
				const nextPath = [...path, index];
				walkDiff(beforeValue[index], afterValue[index], nextPath, items);
			}
			return;
		}

		if (beforeIsObject && afterIsObject) {
			const keys = new Set([
				...Object.keys(beforeValue),
				...Object.keys(afterValue),
			]);
			keys.forEach((key) => {
				const nextPath = [...path, key];
				walkDiff(beforeValue[key], afterValue[key], nextPath, items);
			});
			return;
		}

		pushDiff(items, '修改', path, beforeValue, afterValue);
		return;
	}

	if (beforeValue !== undefined) {
		pushDiff(items, '删除', path, beforeValue, undefined);
		return;
	}

	if (afterValue !== undefined) {
		pushDiff(items, '新增', path, undefined, afterValue);
	}
}

export function buildJsonDiff(
	beforeValue: JsonValue,
	afterValue: JsonValue,
): JsonDiffSummary {
	const items: JsonDiffEntry[] = [];
	walkDiff(beforeValue, afterValue, [], items);

	return {
		items,
		added: items.filter((item) => item.kind === '新增').length,
		removed: items.filter((item) => item.kind === '删除').length,
		changed: items.filter((item) => item.kind === '修改').length,
	};
}
